import { ReactNode, createElement, useState } from "react"
import Head from "next/head"
import Link from "next/link"
import Router, { useRouter } from "next/router"
import { useMutation } from "@blitzjs/rpc"
import { useSession } from "@blitzjs/auth"
import HeaderLogo from "app/core/components/HeaderLogo"
import clsx from "clsx"
import logout from "app/auth/mutations/logout"
import { ImpersonatingUserNotice } from "app/core/components/ImpersonatingUserNotice"
import { stripePromise } from "integrations/stripe/client"
import { Elements } from "@stripe/react-stripe-js"
import { HomeschoolStepper } from "app/bookings/components/HomeschoolStepper"
import { JoinGroupStepper } from "app/bookings/components/JoinGroupStepper"
import { GroupStepper } from "app/bookings/components/GroupStepper"
import toast, { Toaster } from "react-hot-toast"
import { AppLayout } from "./AppLayout"
import { BatchRegistrationStepper } from "app/bookings/components/BatchRegistrationStepper"
import { PageHeader } from "app/core/components/PageHeader"
import { FluencyStepper } from "app/bookings/components/FluencyStepper"
import { MindprintStepper } from "app/bookings/components/MindprintStepper"
import { StarStepper } from "app/bookings/components/StarStepper"

Router.events.on("routeChangeComplete", () => {
  toast.dismiss()
})

type LayoutProps = {
  title?: string
  subtitle?: string
  children: ReactNode
}

export const generateBatchRegistrationGetLayout = () => (page: JSX.Element) => (
  <AppLayout>
    <div className="px-4 pt-2 pb-6 space-y-4 md:py-6 sm:px-6 lg:px-8">
      <PageHeader title="Batch Registration" />
      <div className="rounded-lg shadow bg-white p-6 space-y-6">
        <BatchRegistrationStepper />
        {page}
      </div>
    </div>
  </AppLayout>
)

export const generateHomeschoolGetLayout = ({ title }: { title: string }) => (
  page: JSX.Element
) => (
  <BookingLayout title={title}>
    <HomeschoolStepper />
    {page}
  </BookingLayout>
)

export const generateJoinGroupGetLayout = ({ title }: { title: string }) => (page: JSX.Element) => (
  <BookingLayout title={title}>
    <JoinGroupStepper />
    {page}
  </BookingLayout>
)

export const generateBookGroupGetLayout = ({ title }: { title: string }) => (page: JSX.Element) => (
  <BookingLayout title={title}>
    <GroupStepper />
    {page}
  </BookingLayout>
)

export const generatePrivateSchoolLayout = ({ title }: { title: string }) => (
  page: JSX.Element
) => {
  return (
    <BookingLayout title={title} subtitle="NWEA MAP Growth Test Registration">
      {page}
    </BookingLayout>
  )
}

export const generateReadingFluencyLayout = ({ title }: { title: string }) => (
  page: JSX.Element
) => {
  return (
    <BookingLayout title={title} subtitle="NWEA MAP Reading Fluency Test Registration">
      <FluencyStepper />
      {page}
    </BookingLayout>
  )
}

export const generateMindprintLayout = ({ title }: { title: string }) => (page: JSX.Element) => {
  return (
    <BookingLayout title={title} subtitle="Mindprint Test Registration">
      <MindprintStepper />
      {page}
    </BookingLayout>
  )
}

export const generateStarLayout = ({ title }: { title: string }) => (page: JSX.Element) => {
  return (
    <BookingLayout title={title} subtitle="Renaissance Star Test Registration">
      <StarStepper />
      {page}
    </BookingLayout>
  )
}

export const BookingLayout = ({ title, subtitle, children }: LayoutProps) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false)
  const router = useRouter()
  const session = useSession({ suspense: false })
  const baseUrl = router.pathname.split("/").slice(0, 2).join("/")
  const [logoutMutation] = useMutation(logout, {
    onSuccess() {
      router.push(`${baseUrl}`)
    },
  })

  const AccountButton = ({ mobile }: { mobile?: boolean }) => {
    let classes =
      "px-3 py-2 rounded-md font-medium text-gray-300 hover:text-white hover:bg-gray-700 focus:outline-none focus:text-white focus:bg-gray-700"
    if (mobile) {
      classes += " text-base w-full text-left block"
    } else {
      classes += " text-sm"
    }
    // Replace a specific orderId with "new" in the next URL so that after login
    // the user gets a fresh order lookup rather than a guest draft they can't access.
    const orderId = router.query.orderId as string | undefined
    const loginNext =
      orderId && orderId !== "new" ? router.asPath.replace(`/${orderId}/`, "/new/") : router.asPath

    return session.userId ? (
      <button className={classes} onClick={() => logoutMutation()}>
        Log Out
      </button>
    ) : (
      <Link href={{ pathname: "/login", query: { next: loginNext } }} className={classes}>
        Log In
      </Link>
    )
  }

  const menuLinks = [
    {
      title: "Learn More",
      href: "https://homeschoolboss.com/",
    },
    {
      title: "Contact",
      href: "https://homeschoolboss.com/contact-us/",
    },
    {
      title: "Customer Portal",
      href: session.userId ? "/" : "/login?next=%2f",
    },
  ]

  return (
    <>
      <Head>
        <title>{title || "Homeschool Boss"}</title>
      </Head>

      <ImpersonatingUserNotice />

      <div className="bg-cool-gray-50 min-h-screen">
        <div className="bg-gray-800 pb-32">
          <nav className="bg-gray-800">
            <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
              <div className="border-b border-gray-700">
                <div className="flex items-center justify-between h-16 px-4 sm:px-0">
                  <div className="flex items-center">
                    <HeaderLogo className="mt-3" />

                    <div className="hidden md:block">
                      <div className="ml-10 flex items-baseline space-x-4">
                        {menuLinks.map((o) => (
                          <Link
                            href={o.href}
                            key={o.href}
                            className="px-3 py-2 rounded-md text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 focus:outline-none focus:text-white focus:bg-gray-700"
                          >
                            {o.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="hidden md:block">
                    <div className="ml-4 flex items-center md:ml-6">
                      <AccountButton />
                    </div>
                  </div>
                  <div className="-mr-2 flex md:hidden">
                    {/* Mobile menu button */}
                    <button
                      className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:bg-gray-700 focus:text-white"
                      onClick={() => setMenuIsOpen(!menuIsOpen)}
                    >
                      {menuIsOpen ? (
                        <svg
                          className="h-6 w-6"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="h-6 w-6"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M4 6h16M4 12h16M4 18h16"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div
              className={clsx(
                "border-b border-gray-700 md:hidden",
                menuIsOpen ? "block" : "hidden"
              )}
            >
              <div className="px-2 py-3 space-y-1 sm:px-3">
                {menuLinks.map((o) => (
                  <Link
                    href={o.href}
                    key={o.href}
                    className="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700 focus:outline-none focus:text-white focus:bg-gray-700"
                  >
                    {o.title}
                  </Link>
                ))}
              </div>
              <div className="pt-4 pb-3 border-t border-gray-700">
                <div
                  className="px-2 space-y-1"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="user-menu"
                >
                  <AccountButton mobile={true} />
                </div>
              </div>
            </div>
          </nav>
          <header className="py-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h1 className="text-3xl leading-9 font-bold text-white">
                {subtitle ?? "NWEA MAP Growth Test Online"}
              </h1>
            </div>
          </header>
        </div>

        <main className="-mt-32">
          <div className="max-w-7xl mx-auto pb-12 sm:px-6 lg:px-8">
            <div className="bg-white rounded-lg shadow" key={router.asPath}>
              {createElement(Elements, { stripe: stripePromise }, children)}
            </div>
          </div>
        </main>
      </div>

      <Toaster
        position="top-right"
        toastOptions={{ success: { duration: 5 * 60000 }, error: { duration: 30 * 60000 } }}
      />
    </>
  )
}
